/**
 * Running and deciding a conflict check, in the place the requirement bites.
 *
 * A matter cannot be opened without a *decided* check against its client, and a lead
 * cannot be qualified without a *decided* check against itself. That rule is right and
 * the server enforces it -- `conflict.services.require_cleared` takes a `subject_type`
 * and was always generic. What was missing was any way to satisfy it: this panel used to
 * hardcode `subject_type: 'client'` and take a `clientId`, so a lead -- which has no
 * client yet -- had no screen anywhere that could run a check against it. The
 * requirement was stated and unsatisfiable, which is the worst kind of dead end -- it
 * looks like the product working and behaves like the product broken.
 *
 * So the check happens here, inline, rather than on a screen of its own, generalised
 * over whatever the gate is actually being asked about (`subject_type` +
 * `subject_id`, matching `ConflictCheck.SubjectType` on the backend). Sending somebody
 * away mid-form to find a feature, then back to retype what they had, is how
 * half-finished records get created.
 *
 * Two steps, because they are genuinely two different acts:
 *
 * **The search** looks the subject up against the register of parties the firm has acted
 * for and against. It answers `clear` (nothing found) or `potential` (candidates found).
 * It never answers `conflict` -- a machine does not get to conclude that.
 *
 * **The decision** is a person's, and it is what the gate actually requires. Clearing a
 * search that found nothing is a formality. Proceeding despite candidates requires a
 * written reason, and the file has to show why the firm judged it acceptable. Refusing
 * outright stops the matter or the qualification, whichever this check gates.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
} from '@legalcrm/api';
import { userMessage } from '@legalcrm/api/errors';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  StatusPill,
  Textarea,
} from '@legalcrm/ui';
import { useState } from 'react';

import { formatDateTime } from '@/lib/format';

interface ConflictMatch {
  id: string;
  matched_on: string;
  score: number;
  party?: { id: string; display_name?: string; name?: string } | string | null;
}

export interface ConflictCheck {
  id: string;
  subject_type: string;
  subject_id: string | null;
  outcome: 'clear' | 'potential' | 'conflict';
  is_decided: boolean;
  decided_at: string | null;
  override_reason: string;
  run_at: string;
  match_count: number;
  matches?: ConflictMatch[];
}

//: Mirrors `ConflictCheck.SubjectType` in `backend/domains/conflict/models.py`. Kept as
//: a literal union rather than imported so this panel has no build-time dependency on
//: the backend; a subject type added there and not here fails loudly as a TypeScript
//: error at the call site, not silently at request time.
export type ConflictSubjectType = 'lead' | 'client' | 'case' | 'ad_hoc';

export interface ConflictCheckPanelProps {
  /** What the gate is checking -- must match the `subject_type` the caller's own
   *  server-side gate reads back (`require_cleared("lead", ...)`, `("client", ...)`). */
  subjectType: ConflictSubjectType;
  subjectId: string;
  /** Searched against the register, and shown on the record as what was searched. */
  subjectName: string;
  /** Completes "Required before ...". Left to the caller because what a decided check
   *  unblocks differs by screen -- opening a matter, qualifying a lead -- and a panel
   *  that guessed would eventually guess wrong. */
  requirementLabel: string;
  /** Told when a decided check exists, so the caller can stop blocking. */
  onDecided?: (check: ConflictCheck) => void;
}

const OUTCOME_TONE = {
  clear: 'success',
  potential: 'warning',
  conflict: 'danger',
} as const;

function partyName(match: ConflictMatch): string {
  const party = match.party;
  if (!party) return 'A party on the register';
  if (typeof party === 'string') return party;
  return party.display_name ?? party.name ?? 'A party on the register';
}

export function ConflictCheckPanel({
  subjectType,
  subjectId,
  subjectName,
  requirementLabel,
  onDecided,
}: ConflictCheckPanelProps) {
  const api = useApi();
  const [reason, setReason] = useState('');

  const invalidates = [
    qk.list('conflict-checks', { subject_type: subjectType, subject_id: subjectId }),
  ];

  // Newest first, so `results[0]` is the check that counts.
  const checks = useApiQuery<{ results: ConflictCheck[] } | ConflictCheck[]>(
    qk.list('conflict-checks', { subject_type: subjectType, subject_id: subjectId }),
    STAFF.conflictChecks,
    {
      query: { subject_type: subjectType, subject_id: subjectId },
      enabled: Boolean(subjectId),
    },
  );

  const rows = Array.isArray(checks.data) ? checks.data : (checks.data?.results ?? []);
  const latest = rows[0];
  const decided = latest?.is_decided ? latest : undefined;

  const run = useApiMutation<ConflictCheck, void>(
    {
      mutationFn: () =>
        api.post<ConflictCheck>(`${STAFF.conflictChecks}/`, {
          subject_type: subjectType,
          subject_id: subjectId,
          // The subject's own name is what the register is searched on. Sending it from
          // here rather than letting the server look it up keeps the searched terms
          // visible on the record: "what was actually searched" is the question asked
          // when a check is challenged later.
          names: [subjectName],
        }),
      invalidates,
    },
    { onSuccess: () => setReason('') },
  );

  const decide = useApiMutation<ConflictCheck, { outcome: string; override_reason?: string }>(
    {
      mutationFn: (body) =>
        api.post<ConflictCheck>(`${STAFF.conflictCheckDecide(latest?.id ?? '')}/`, body),
      invalidates,
    },
    { onSuccess: (check) => onDecided?.(check) },
  );

  if (!subjectId) return null;

  // ---- already decided: say so and get out of the way ----------------------
  if (decided) {
    return (
      <Card>
        <CardHeader
          title="Conflict check"
          actions={
            <StatusPill
              label={
                decided.outcome === 'clear'
                  ? 'Cleared'
                  : decided.outcome === 'potential'
                    ? 'Proceeding with a reason'
                    : 'Conflict found'
              }
              tone={OUTCOME_TONE[decided.outcome]}
            />
          }
        />
        <CardBody>
          <p className="text-sm text-ink-muted">
            Decided {decided.decided_at ? formatDateTime(decided.decided_at) : ''}.
            {decided.outcome === 'conflict' &&
              ' This blocks the next step while that stands.'}
          </p>
          {decided.override_reason && (
            <p className="mt-2 text-sm text-ink">
              <span className="text-ink-muted">Reason recorded: </span>
              {decided.override_reason}
            </p>
          )}
        </CardBody>
      </Card>
    );
  }

  // ---- run, then decide ----------------------------------------------------
  return (
    <Card>
      <CardHeader
        title="Conflict check"
        description={`Required before ${requirementLabel}.`}
        actions={
          !latest && (
            <Button
              variant="primary"
              size="sm"
              loading={run.isPending}
              onClick={() => run.mutate()}
            >
              Run the check
            </Button>
          )
        }
      />

      <CardBody>
        {!latest && !run.isPending && (
          <p className="text-sm text-ink-muted">
            Searches every party this firm has acted for or against for a name matching
            {' '}{subjectName || 'this record'}. Nothing is decided by the search itself.
          </p>
        )}

        {Boolean(run.error || decide.error) && (
          <p role="alert" className="mb-3 text-sm text-danger">
            {userMessage(run.error ?? decide.error)}
          </p>
        )}

        {latest && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <StatusPill
                label={latest.outcome === 'clear' ? 'Nothing found' : 'Possible matches'}
                tone={OUTCOME_TONE[latest.outcome]}
              />
              <span className="text-sm text-ink-muted">
                Searched {formatDateTime(latest.run_at)}
              </span>
            </div>

            {latest.outcome === 'potential' && latest.matches?.length ? (
              <ul className="flex flex-col divide-y rounded border">
                {latest.matches.map((match) => (
                  <li
                    key={match.id}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <span className="truncate text-sm text-ink">{partyName(match)}</span>
                    <Badge tone="neutral" size="sm">
                      matched on {match.matched_on}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : null}

            {latest.outcome === 'potential' && (
              <Textarea
                label="Why is it acceptable to proceed"
                rows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="e.g. the matched party is a different person of the same name"
                hint="Required, and kept on the file permanently. The record has to show why the firm judged this acceptable."
              />
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                size="sm"
                loading={decide.isPending}
                disabled={latest.outcome === 'potential' && !reason.trim()}
                onClick={() =>
                  decide.mutate(
                    latest.outcome === 'clear'
                      ? { outcome: 'clear' }
                      : { outcome: 'potential', override_reason: reason.trim() },
                  )
                }
              >
                {latest.outcome === 'clear' ? 'Confirm clear' : 'Proceed anyway'}
              </Button>

              <Button
                variant="secondary"
                size="sm"
                loading={decide.isPending}
                onClick={() => decide.mutate({ outcome: 'conflict' })}
              >
                Record a conflict
              </Button>
            </div>

            <p className="text-xs text-ink-subtle">
              Recording a conflict blocks the next step. It stays on the file as the
              reason the firm did not act.
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
